<!-- =========================================================================================
    File Name: ForgotPassword.vue
    Description: Forgot Password Page with improved UX
========================================================================================== -->
<template>
  <div class="h-screen flex w-full bg-img">
    <div class="vx-col w-4/5 sm:w-3/4 md:w-3/5 lg:w-2/5 mx-auto self-center">
      <vx-card>
        <div slot="no-body" class="full-page-bg-color">
          <div class="vx-row">
            <div class="vx-col w-full mx-auto self-center d-theme-dark-bg">
              <div class="p-8">
                <div class="vx-card__title mb-8 text-center">
                  <h4 class="mb-4">비밀번호 찾기</h4>
                  <p>이메일 주소를 입력하시면 비밀번호 재설정 방법에 대한 안내를 보내드립니다.</p>
                </div>

                <form @submit.prevent="handleSendEmail" class="my-8">
                  <ml-input
                    v-if="enableUsername"
                    type="text"
                    placeholder="사용자명 (아이디)"
                    v-model="username"
                    class="w-full mb-2"
                    :class="{'error-input': errors.username}"
                    @input="handleUsernameInput"
                    @blur="handleUsernameBlur"
                  />
                  <p v-if="errors.username && enableUsername && touched.username" class="text-danger text-sm mb-4">{{ errors.username }}</p>

                  <ml-input
                    type="email"
                    placeholder="이메일 주소"
                    v-model="email"
                    class="w-full mb-2"
                    :class="{'error-input': errors.email}"
                    @input="handleEmailInput"
                    @blur="handleEmailBlur"
                    required
                  />
                  <p v-if="errors.email && touched.email" class="text-danger text-sm mb-4">{{ errors.email }}</p>

                  <div class="flex flex-col md:flex-row justify-between mt-8 space-y-4 md:space-y-0">
                    <vs-button
                      type="border"
                      to="/login"
                      class="px-4 w-full md:w-auto"
                    >
                      로그인으로 돌아가기
                    </vs-button>
                    <vs-button
                      class="px-4 w-full md:w-auto"
                      :disabled="!isValidForm"
                      @click="handleSendEmail"
                    >
                      비밀번호 변경 이메일 받기
                    </vs-button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      email: '',
      username: '',
      errors: {
        email: '',
        username: ''
      },
      touched: {
        email: false,
        username: false
      },
      isLoading: false,
      enableUsername: true // username 입력 필드 활성화 여부
    }
  },

  computed: {
    isValidForm() {
      const emailValid = this.email && !this.errors.email
      const usernameValid = !this.enableUsername || (this.username && !this.errors.username)
      return emailValid && usernameValid
    }
  },

  methods: {
    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!this.email) {
        this.errors.email = '이메일 주소를 입력해주세요.'
      } else if (!emailRegex.test(this.email)) {
        this.errors.email = '올바른 이메일 형식이 아닙니다.'
      } else {
        this.errors.email = ''
      }
    },

    validateUsername() {
      if (this.enableUsername) {
        if (!this.username) {
          this.errors.username = '사용자명을 입력해주세요.'
        } else if (this.username.length < 3) {
          this.errors.username = '사용자명은 3자 이상 입력해주세요.'
        } else if (!/^[a-zA-Z0-9_.!]+$/.test(this.username)) {
          this.errors.username = '사용자명은 영문, 숫자, 언더스코어(_), 마침표(.), 느낌표(!)만 사용 가능합니다.'
        } else {
          this.errors.username = ''
        }
      }
    },

    handleUsernameInput() {
      // 터치된 필드에서만 실시간 검증
      if (this.touched.username) {
        this.validateUsername()
      }
    },

    handleUsernameBlur() {
      // 포커스 아웃 시 touched 설정하고 검증
      this.touched.username = true
      this.validateUsername()
    },

    handleEmailInput() {
      // 터치된 필드에서만 실시간 검증
      if (this.touched.email) {
        this.validateEmail()
      }
    },

    handleEmailBlur() {
      // 포커스 아웃 시 touched 설정하고 검증
      this.touched.email = true
      this.validateEmail()
    },

    async handleSendEmail() {
      // 모든 필드를 touched로 설정하여 에러 표시
      this.touched.email = true
      if (this.enableUsername) {
        this.touched.username = true
      }

      // 모든 필드 검증
      this.validateEmail()
      if (this.enableUsername) {
        this.validateUsername()
      }

      if (!this.isValidForm || this.isLoading) return

      try {
        this.isLoading = true
        const payload = { email: this.email }
        if (this.enableUsername && this.username) {
          payload.username = this.username
        }
        await this.$store.dispatch('USER_FORGOT_PASSWORD', payload)
        this.$vs.notify({
          title: '이메일 발송 완료',
          text: '비밀번호 재설정 안내 이메일을 발송했습니다. 메일함을 확인해주세요.',
          color: 'success'
        })
        this.$router.push('/reset-password').then();
      } catch (error) {
        this.$vs.notify({
          title: '오류',
          text: '이메일 발송에 실패했습니다. 입력하신 이메일 주소를 확인해주세요.',
          color: 'danger'
        })

        const customAlert = new CustomEvent("customAlert", {
          detail: {
            title: "오류가 발생했습니다",
            message: error.response.data ? error.response.data : "알 수 없는 오류가 발생했습니다",
          },
        });
        window.dispatchEvent(customAlert);
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.error-input {
  border-color: var(--vs-danger) !important;
}
</style>
